/**
 * CopilotFrontendTools.tsx
 *
 * Level 3 frontend tools: UI-only navigation/form triggers the copilot agent
 * can invoke. No apiClient/service calls here — mutations stay agent-side;
 * these tools only navigate() or set local UI state, then return a short
 * human-readable result string that becomes the follow-up turn's tool result.
 *
 * Must be mounted once inside <CopilotKitProvider>, sibling to
 * CopilotToolRenderers.
 */

import { useFrontendTool } from "@copilotkit/react-core/v2";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const NAV_PAGES = [
  "my-onething",
  "priorities",
  "okrs",
  "my-team",
  "org-chart",
  "profile",
  "home",
] as const;

const ROUTE_BY_PAGE: Record<(typeof NAV_PAGES)[number], string> = {
  "my-onething": "/my-onething",
  priorities: "/priorities",
  okrs: "/okrs",
  "my-team": "/my-team",
  "org-chart": "/org-chart",
  profile: "/profile",
  home: "/",
};

export function CopilotFrontendTools() {
  const navigate = useNavigate();

  useFrontendTool(
    {
      name: "navigate_to_page",
      description: "Navigate the user to a top-level page in the OneThing app.",
      parameters: z.object({ page: z.enum(NAV_PAGES) }),
      handler: async ({ page }) => {
        navigate(ROUTE_BY_PAGE[page]);
        return `Navigated to the ${page} page.`;
      },
    },
    [navigate],
  );

  useFrontendTool(
    {
      name: "open_priority_detail",
      description: "Open the detail page for a specific priority by its pid.",
      parameters: z.object({ priorityPid: z.string().min(1) }),
      handler: async ({ priorityPid }) => {
        navigate(`/priorities/${priorityPid}`);
        return `Opening priority ${priorityPid}.`;
      },
    },
    [navigate],
  );

  return null;
}
